import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';

const ProLoginPage = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Register form state
  const [registerData, setRegisterData] = useState({
    company_name: '',
    siret: '',
    nom: '',
    prenom: '',
    email: '',
    password: '',
    confirm_password: '',
    phone: '',
    address: '',
    postal_code: '',
    city: '',
    activity: '',
    annual_installations: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/pro/login`, loginData);
      
      // Store token and user data
      localStorage.setItem('pro_token', response.data.access_token);
      localStorage.setItem('pro_user', JSON.stringify(response.data.user));
      
      // Redirect to PRO dashboard
      navigate('/pro/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate passwords match
    if (registerData.password !== registerData.confirm_password) {
      setError('Les mots de passe ne correspondent pas');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/pro/register`, registerData);
      
      setSuccess(response.data.message);
      
      // Clear form
      setRegisterData({
        company_name: '',
        siret: '',
        nom: '',
        prenom: '',
        email: '',
        password: '',
        confirm_password: '',
        phone: '',
        address: '',
        postal_code: '',
        city: '',
        activity: '',
        annual_installations: ''
      });

      // Optionally switch to login after 3 seconds
      setTimeout(() => {
        setIsLogin(true);
        setSuccess('');
      }, 5000);

    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Logo showTagline={true} clickable={true} />
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Title Section */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Zone réservée aux installateurs - Tarifs préférentiels
          </h1>
          <p className="text-lg text-gray-600">
            Vous installez régulièrement des toiles de stores : Ouvrez un compte
          </p>
          <div className="flex items-center justify-center gap-4 mt-4">
            <span className="inline-block bg-celadon text-white px-4 py-2 rounded font-bold">
              🇫🇷 MADE IN FRANCE
            </span>
            <span className="text-gray-700">Fabricant français - Prix direct usine</span>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="max-w-6xl mx-auto mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="max-w-6xl mx-auto mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {/* Forms Container */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-white p-8 rounded-lg shadow-lg border-4 border-yellow-400">
          {/* Login Form */}
          <div className="border-r border-gray-200 pr-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">se connecter</h2>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">mél</label>
                <input
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">mot de passe</label>
                <input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-celadon text-white py-2 rounded font-medium hover:bg-celadon-light transition-colors disabled:opacity-50"
              >
                {loading ? 'Connexion...' : 'se connecter'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/pro/forgot-password')}
                  className="text-celadon text-sm hover:underline"
                >
                  mot de passe oublié ?
                </button>
              </div>
            </form>
          </div>

          {/* Divider with "ou" */}
          <div className="hidden md:flex absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white px-4 py-2 text-gray-500 font-bold">
            ou
          </div>

          {/* Register Form */}
          <div className="pl-0 md:pl-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">s'inscrire</h2>
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SOCIETE ou NOM COMMERCIAL*</label>
                <input
                  type="text"
                  value={registerData.company_name}
                  onChange={(e) => setRegisterData({ ...registerData, company_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom*</label>
                  <input
                    type="text"
                    value={registerData.nom}
                    onChange={(e) => setRegisterData({ ...registerData, nom: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom*</label>
                  <input
                    type="text"
                    value={registerData.prenom}
                    onChange={(e) => setRegisterData({ ...registerData, prenom: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mél*</label>
                <input
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe*</label>
                <input
                  type="password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmez votre mot de passe*</label>
                <input
                  type="password"
                  value={registerData.confirm_password}
                  onChange={(e) => setRegisterData({ ...registerData, confirm_password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Siret*</label>
                <input
                  type="text"
                  value={registerData.siret}
                  onChange={(e) => setRegisterData({ ...registerData, siret: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                  required
                  pattern="[0-9]{14}"
                  title="Le SIRET doit contenir 14 chiffres"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Activité*</label>
                <input
                  type="text"
                  value={registerData.activity}
                  onChange={(e) => setRegisterData({ ...registerData, activity: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Combien de stores installez-vous chaque année?
                </label>
                <input
                  type="text"
                  value={registerData.annual_installations}
                  onChange={(e) => setRegisterData({ ...registerData, annual_installations: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Portable*</label>
                <input
                  type="tel"
                  value={registerData.phone}
                  onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse*</label>
                <input
                  type="text"
                  value={registerData.address}
                  onChange={(e) => setRegisterData({ ...registerData, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code post.*</label>
                  <input
                    type="text"
                    value={registerData.postal_code}
                    onChange={(e) => setRegisterData({ ...registerData, postal_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                    required
                    pattern="[0-9]{5}"
                    title="Le code postal doit contenir 5 chiffres"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ville*</label>
                  <input
                    type="text"
                    value={registerData.city}
                    onChange={(e) => setRegisterData({ ...registerData, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-celadon text-white py-2 rounded font-medium hover:bg-celadon-light transition-colors disabled:opacity-50"
              >
                {loading ? 'Inscription...' : 's\'inscrire'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProLoginPage;
